/**
 * Returns 's' if the value is greater than 1, empty string otherwise
 * @param value - number or string to check for plurality
 * @returns string - 's' or ''
 */
export const pluralByNumber = (value?: number | string): string => {
  if (value === undefined) return '';
  const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
  return !isNaN(numValue) && numValue > 1 ? 's' : '';
};

export const sentenceCase = (str: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const manipulateRequestableType = (str: string): string => {
  if (!str) return '';
  return str
    .split('-')
    .map(word => sentenceCase(word))
    .join(' ');
};

export const formatStationType = (type: string) => {
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
// Helper function to handle null strings
export const sanitizeNullString = (
  value: string | null | undefined,
): string | null => {
  if (!value || value === 'null' || value === 'NULL') {
    return null;
  }
  return value;
};

/**
 * Formats a 10-digit US phone number string as (XXX) XXX-XXXX.
 * If the input is not a 10-digit number, returns the original value.
 * @param value - The phone number string to format
 * @returns The formatted phone number or the original value
 */
export const formattingPhoneNumberToUS_Format = (
  value: string | null | undefined,
): string | null => {
  if (!value) return null;
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return value;
};

/**
 * Normalizes any 10-digit US phone number into the exact "(999) 999-9999" shape
 * the masked inputs and the API both expect.
 *
 * `react-input-mask` re-formats the `value` prop for *display* but never fires
 * `onChange` for it, so a number seeded from HubSpot or from a saved sponsor
 * (e.g. "+1 817-745-3000") renders as a tidy masked number while React state
 * still holds the raw string - and validation then fails against a field that
 * looks perfectly correct. Run seeded values through this before they reach
 * state so display and state always agree.
 *
 * @param value - The phone number in any common presentation
 * @returns The masked number, or null when it is not a 10-digit US number
 */
export const normalizePhoneToUsMask = (
  value: string | null | undefined,
): string | null => {
  if (!value) return null;
  let digits = value.replace(/\D/g, '');
  // Tolerate the "1" country code that HubSpot and E.164 values carry.
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  if (digits.length !== 10) return null;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/**
 * Same as {@link normalizePhoneToUsMask}, but hands back the original value when
 * it cannot be normalized so the user still sees - and can correct - whatever
 * was stored, rather than the field silently emptying itself.
 */
export const normalizePhoneForInput = (
  value: string | null | undefined,
): string => normalizePhoneToUsMask(value) ?? value ?? '';

/**
 * Sanitizes user input to prevent XSS attacks by removing potentially harmful characters
 * @param value - The string to sanitize
 * @returns The sanitized string
 */

export const sanitizeSearchInput = (
  value: string | null | undefined,
): string => {
  if (!value) return '';
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .trim();
};
